/**
 * Helpers.js
 * 
 * This module provides utility functions for 3D geometry operations in the SightFlight Designer application.
 * It contains helper functions for:
 * 1. Creating wireframe visualizations for panels
 * 2. Extracting vertices from 3D objects
 * 3. Calculating edge directions for proper alignment
 * 4. Computing rotation quaternions for orientation adjustments
 * 
 * These geometric utility functions support panel placement and visualization
 * throughout the application.
 */

import * as THREE from 'three';
import { COLORS, LINES } from '../Global/materials';
import { createEdgesWireframe } from "../Global/functions";
import { getAzimuth } from '../Segments/AreaPitchAzimu';
import { RENDER_LAYERS } from "../Global/variables";

const OVERLAP_EPS = 1e-6;
const AZIMUTH_EPS = 1e-10;

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeAzimuth = (value) => {
  const parsed = toFiniteNumber(value, 0);
  const normalized = ((parsed % 360) + 360) % 360;
  return Number(normalized.toFixed(2));
};

export const applyBackFacePattern = (material, options = {}) => {
  if (!material) return material;
  if (material.userData?.backFacePatternApplied) return material;

  const stripeScale = toFiniteNumber(options.stripeScale, 30);
  const baseColor = new THREE.Color(options.baseColor ?? 0x1f252c);
  const stripeColor = new THREE.Color(options.stripeColor ?? COLORS.yellow);
  const previousOnBeforeCompile = material.onBeforeCompile;

  material.onBeforeCompile = (shader) => {
    if (typeof previousOnBeforeCompile === "function") {
      previousOnBeforeCompile(shader);
    }

    shader.uniforms.backFaceBaseColor = { value: baseColor };
    shader.uniforms.backFaceStripeColor = { value: stripeColor };
    shader.uniforms.backFaceStripeScale = { value: stripeScale };

    const hasMapFragment = shader.fragmentShader.includes("#include <map_fragment>");
    const hasColorFragment = shader.fragmentShader.includes("#include <color_fragment>");
    const uvToken = shader.fragmentShader.includes("vMapUv")
      ? "vMapUv"
      : shader.fragmentShader.includes("vUv")
        ? "vUv"
        : null;

    const stripeExpr = uvToken
      ? `fract((${uvToken}.x + ${uvToken}.y) * backFaceStripeScale)`
      : `fract((gl_FragCoord.x + gl_FragCoord.y) * 0.03 * backFaceStripeScale)`;

    const backFaceCode = `
  if (!gl_FrontFacing) {
    float stripe = step(0.5, ${stripeExpr});
    diffuseColor.rgb = mix(backFaceBaseColor, backFaceStripeColor, stripe);
  }`;

    let nextFragmentShader =
      `uniform vec3 backFaceBaseColor;\n` +
      `uniform vec3 backFaceStripeColor;\n` +
      `uniform float backFaceStripeScale;\n` +
      shader.fragmentShader;

    if (hasMapFragment) {
      nextFragmentShader = nextFragmentShader.replace(
        "#include <map_fragment>",
        `#include <map_fragment>${backFaceCode}`
      );
    } else if (hasColorFragment) {
      nextFragmentShader = nextFragmentShader.replace(
        "#include <color_fragment>",
        `#include <color_fragment>${backFaceCode}`
      );
    } else {
      // Fail-safe: keep panel rendering untouched if expected shader anchors are absent.
      return;
    }

    shader.fragmentShader = nextFragmentShader;
  };

  material.userData = {
    ...(material.userData || {}),
    backFacePatternApplied: true,
  };
  material.needsUpdate = true;

  return material;
};

const getProjectionFn = (normal) => {
  const absNormal = new THREE.Vector3(
    Math.abs(normal.x),
    Math.abs(normal.y),
    Math.abs(normal.z)
  );

  if (absNormal.x >= absNormal.y && absNormal.x >= absNormal.z) {
    return (v) => ({ x: v.y, y: v.z }); // YZ
  }
  if (absNormal.y >= absNormal.x && absNormal.y >= absNormal.z) {
    return (v) => ({ x: v.x, y: v.z }); // XZ
  }
  return (v) => ({ x: v.x, y: v.y }); // XY
};

const dedupePoints2D = (points, eps = OVERLAP_EPS) => {
  const unique = [];
  for (const p of points) {
    const exists = unique.some((u) => {
      const dx = u.x - p.x;
      const dy = u.y - p.y;
      return dx * dx + dy * dy <= eps * eps;
    });
    if (!exists) unique.push(p);
  }
  return unique;
};

const orderPoints2D = (points) => {
  const center = points.reduce(
    (acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }),
    { x: 0, y: 0 }
  );
  center.x /= points.length;
  center.y /= points.length;

  return [...points].sort(
    (a, b) =>
      Math.atan2(a.y - center.y, a.x - center.x) -
      Math.atan2(b.y - center.y, b.x - center.x)
  );
};

const buildPolygon2D = (points3d, project) => {
  if (!Array.isArray(points3d) || points3d.length < 3) return [];
  const projected = points3d.map(project);
  const unique = dedupePoints2D(projected);
  if (unique.length < 3) return [];
  return orderPoints2D(unique);
};

const isPointOnSegment2D = (p, a, b, eps = OVERLAP_EPS) => {
  const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  if (Math.abs(cross) > eps) return false;

  const dot = (p.x - a.x) * (p.x - b.x) + (p.y - a.y) * (p.y - b.y);
  return dot <= eps;
};

const segmentsIntersect2D = (p1, p2, q1, q2, eps = OVERLAP_EPS) => {
  const orientation = (a, b, c) => {
    const val = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
    if (Math.abs(val) <= eps) return 0;
    return val > 0 ? 1 : 2;
  };

  const onSegment = (a, b, c) =>
    Math.min(a.x, c.x) - eps <= b.x &&
    b.x <= Math.max(a.x, c.x) + eps &&
    Math.min(a.y, c.y) - eps <= b.y &&
    b.y <= Math.max(a.y, c.y) + eps;

  const o1 = orientation(p1, p2, q1);
  const o2 = orientation(p1, p2, q2);
  const o3 = orientation(q1, q2, p1);
  const o4 = orientation(q1, q2, p2);

  if (o1 !== o2 && o3 !== o4) return true;

  if (o1 === 0 && onSegment(p1, q1, p2)) return true;
  if (o2 === 0 && onSegment(p1, q2, p2)) return true;
  if (o3 === 0 && onSegment(q1, p1, q2)) return true;
  if (o4 === 0 && onSegment(q1, p2, q2)) return true;

  return false;
};

const pointInPolygon2D = (point, polygon, eps = OVERLAP_EPS) => {
  if (!polygon.length) return false;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (isPointOnSegment2D(point, a, b, eps)) return true;
  }

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || eps) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
};

const polygonsOverlap2D = (polyA, polyB, eps = OVERLAP_EPS) => {
  if (polyA.length < 3 || polyB.length < 3) return false;

  for (let i = 0; i < polyA.length; i++) {
    const a1 = polyA[i];
    const a2 = polyA[(i + 1) % polyA.length];
    for (let j = 0; j < polyB.length; j++) {
      const b1 = polyB[j];
      const b2 = polyB[(j + 1) % polyB.length];
      if (segmentsIntersect2D(a1, a2, b1, b2, eps)) return true;
    }
  }

  if (pointInPolygon2D(polyA[0], polyB, eps)) return true;
  if (pointInPolygon2D(polyB[0], polyA, eps)) return true;

  return false;
};

export const polygonsOverlapOnPlane = (pointsA, pointsB, normal, origin = null) => {
  if (!Array.isArray(pointsA) || !Array.isArray(pointsB)) return false;
  if (pointsA.length < 3 || pointsB.length < 3) return false;

  const planePoint = origin ?? pointsB[0] ?? pointsA[0];
  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
    normal.clone().normalize(),
    planePoint
  );
  const projectedA = pointsA.map((p) => plane.projectPoint(p, new THREE.Vector3()));
  const projectedB = pointsB.map((p) => plane.projectPoint(p, new THREE.Vector3()));

  const projectRaw = getProjectionFn(normal);
  const origin2d = projectRaw(planePoint);
  const project = (v) => {
    const p = projectRaw(v);
    return { x: p.x - origin2d.x, y: p.y - origin2d.y };
  };

  const polyA = buildPolygon2D(projectedA, project);
  const polyB = buildPolygon2D(projectedB, project);

  return polygonsOverlap2D(polyA, polyB);
};

export const getObstacleBoundaryPoints = (obstruction) => {
  if (!obstruction) return [];
  if (obstruction.mesh?.updateMatrixWorld) {
    obstruction.mesh.updateMatrixWorld(true);
  }
  const storedOffsetOutline = obstruction?.mesh?.userData?.obstacleOffsetOutlineWorld;
  if (Array.isArray(storedOffsetOutline) && storedOffsetOutline.length >= 3) {
    return storedOffsetOutline.map((p) => p.clone());
  }
  const offset = obstruction.mesh?.getObjectByName?.("offset");
  if (offset?.geometry?.attributes?.position) {
    if (offset.updateMatrixWorld) {
      offset.updateMatrixWorld(true);
    }
    return getVertices(offset);
  }
  if (Array.isArray(obstruction.cords)) {
    return obstruction.cords;
  }
  return [];
};

export const clampPanelTilt = (tilt) => {
  return THREE.MathUtils.clamp(toFiniteNumber(tilt, 0), 0, 90);
};

export const normalizePanelRotation = (rotation) => {
  return THREE.MathUtils.clamp(toFiniteNumber(rotation, 0), -180, 180);
};

export const getPanelTiltLiftOffset = (panelHeight, tilt) => {
  const safeHeight = Math.max(0, toFiniteNumber(panelHeight, 0));
  const tiltRad = THREE.MathUtils.degToRad(clampPanelTilt(tilt));
  return (safeHeight / 2) * Math.sin(Math.abs(tiltRad));
};

export const getPanelFacingAzimuth = (quaternion, fallbackAzimuth = 0) => {
  if (!(quaternion instanceof THREE.Quaternion)) {
    return normalizeAzimuth(fallbackAzimuth);
  }

  // Panel facing azimuth should be based on the panel normal (+Z), not panel edge (+Y).
  // This keeps azimuth stable under in-plane rotation while allowing tilt to change it.
  const panelNormal = new THREE.Vector3(0, 0, 1).applyQuaternion(quaternion);
  const horizontal = new THREE.Vector3(panelNormal.x, panelNormal.y, 0);

  if (horizontal.lengthSq() <= AZIMUTH_EPS) {
    return normalizeAzimuth(fallbackAzimuth);
  }

  horizontal.normalize();

  try {
    return normalizeAzimuth(getAzimuth(new THREE.Vector3(0, 0, 0), horizontal));
  } catch (error) {
    // Fallback when geo-projection metadata is unavailable.
    const angleDeg = THREE.MathUtils.radToDeg(Math.atan2(horizontal.x, horizontal.y));
    return normalizeAzimuth(angleDeg);
  }
};

export const buildPanelQuaternion = (
  orientation,
  edgeDirection,
  {
    tilt = 0,
    rotation = 0,
  } = {}
) => {
  const up = new THREE.Vector3(0, 0, 1);
  const safeOrientation = orientation.clone().normalize();

  const baseQuaternion = new THREE.Quaternion().setFromUnitVectors(up, safeOrientation);
  const safeEdgeDirection =
    edgeDirection && edgeDirection.lengthSq() > AZIMUTH_EPS
      ? edgeDirection.clone().normalize()
      : new THREE.Vector3(1, 0, 0).applyQuaternion(baseQuaternion).normalize();

  const [, alignQuaternion] = getRotation(safeOrientation, safeEdgeDirection, baseQuaternion);
  const alignedQuaternion = alignQuaternion.clone().multiply(baseQuaternion).normalize();

  const rotationRad = THREE.MathUtils.degToRad(normalizePanelRotation(rotation));
  const inPlaneRotation = new THREE.Quaternion().setFromAxisAngle(safeOrientation, rotationRad);
  const rotatedQuaternion = inPlaneRotation.clone().multiply(alignedQuaternion).normalize();

  // Tilt around the panel's local X axis after in-plane rotation.
  const tiltAxis = new THREE.Vector3(1, 0, 0).applyQuaternion(rotatedQuaternion).normalize();
  // Positive UI tilt should raise the panel "up", not down.
  const tiltRad = THREE.MathUtils.degToRad(-clampPanelTilt(tilt));
  const tiltQuaternion = new THREE.Quaternion().setFromAxisAngle(tiltAxis, tiltRad);

  return tiltQuaternion.clone().multiply(rotatedQuaternion).normalize();
};

export const resolvePanelTransform = (
  planeNormal,
  edgeDirection,
  panelData = {}
) => {
  const orientation = planeNormal.clone().normalize();
  if (orientation.z < 0) {
    orientation.negate();
  }

  const tilt = clampPanelTilt(panelData.tilt);
  const rotation = normalizePanelRotation(panelData.rotation);
  const quaternion = buildPanelQuaternion(orientation, edgeDirection, { tilt, rotation });
  const azimuth = getPanelFacingAzimuth(quaternion, panelData.azimuth);

  return {
    orientation,
    tilt,
    rotation,
    azimuth,
    quaternion,
  };
};

/**
 * Creates a wireframe outline for a panel
 * 
 * This function extracts the vertices of a panel, reorders them to form a proper
 * rectangular outline, and creates a wireframe visualization that's added as a
 * child to the panel. The wireframe is used for visual feedback during panel
 * placement and selection.
 * 
 * @param {THREE.Mesh} panel - The panel mesh to create a wireframe for
 */
export function createWireframe(panel) {
  // Build wireframe in panel-local space because it is parented to the panel.
  // Using world-space vertices here causes double transforms after panel updates.
  const positionAttribute = panel?.geometry?.attributes?.position;
  if (!positionAttribute) return;
  const vertices = [];
  for (let i = 0; i < positionAttribute.count; i++) {
    vertices.push(new THREE.Vector3().fromBufferAttribute(positionAttribute, i));
  }
  if (vertices.length < 3) return;
  
  // Reorder vertices to ensure a rectangular outline
  // This handles the case where vertices from a PlaneGeometry aren't in the expected order
  const last = vertices.splice(2, 1);
  vertices.push(last[0]);
  
  // Create an array of points for each edge in the wireframe
  const edgePoints = [];
  for (let i = 0; i < vertices.length; i++) {
    // Calculate the index of the next vertex (wrapping around at the end)
    let index = (i + 1) % vertices.length;
    const point1 = vertices[i];
    const point2 = vertices[index];

    // Add both points to form a line segment
    edgePoints.push(point1);
    edgePoints.push(point2);
  }
  
  // Clone the red material for the wireframe
  const wireMat = LINES.redMaterial.clone();
  
  // Create a wireframe using the edge points and material
  const edgeWireframe = createEdgesWireframe(edgePoints, wireMat);
  edgeWireframe.userData = {
    ...(edgeWireframe.userData || {}),
    isPanelWireframe: true,
  };
  
  // Make the wireframe transparent initially (will be made visible when selected)
  edgeWireframe.material.setValues({ transparent: true, opacity: 0 });
  
  // Set high render order to ensure the wireframe is visible on top of the panel
  edgeWireframe.renderOrder = RENDER_LAYERS.PANEL_OUTLINE;
  
  // Add the wireframe as a child of the panel for easier management
  panel.add(edgeWireframe);
}

export function getPanelWireframe(panel) {
  if (!panel || !Array.isArray(panel.children)) return null;

  const byMarker = panel.children.find(
    (child) =>
      child?.userData?.isPanelWireframe === true ||
      child?.name === "wireframe"
  );
  if (byMarker) return byMarker;

  // Fallback for panels built before the marker existed. The snap grid is also
  // a LineSegments parented to a panel, so exclude it -- colouring the grid
  // instead of the outline would leave the panel with no visible boundary.
  return panel.children.find(
    (child) => child?.isLineSegments && child?.material && child?.name !== "grid"
  ) || null;
}

export function ensurePanelWireframe(panel) {
  if (!panel) return null;
  const existing = getPanelWireframe(panel);
  if (existing) return existing;
  createWireframe(panel);
  return getPanelWireframe(panel);
}

const PANEL_OUTLINE_STYLE = {
  transparent: true,
  depthTest: false,
  depthWrite: false,
};

/**
 * Applies outline styling (opacity/color) to a panel's wireframe, creating the
 * wireframe and normalizing its render state first.
 *
 * @param {THREE.Mesh} panel - The panel whose outline should be updated
 * @param {object} values - Material values, e.g. { opacity: 1, color: COLORS.yellow }
 * @returns {THREE.LineSegments|null} The styled wireframe, or null if unavailable
 */
export function setPanelWireframeStyle(panel, values = {}) {
  const wireframe = ensurePanelWireframe(panel);
  if (!wireframe?.material?.setValues) return null;

  if (wireframe.userData?.panelOutlineStyled !== true) {
    wireframe.material.setValues(PANEL_OUTLINE_STYLE);
    wireframe.material.needsUpdate = true;
    wireframe.userData = {
      ...(wireframe.userData || {}),
      panelOutlineStyled: true,
    };
  }

  wireframe.material.setValues(values);
  return wireframe;
}

/**
 * Extracts vertices from a 3D object in world space
 * 
 * This function takes a THREE.js object with geometry and extracts all of its
 * vertices, converting them from local object space to world space coordinates.
 * 
 * @param {THREE.Mesh} object - The 3D object to extract vertices from
 * @returns {Array<THREE.Vector3>} Array of vertices in world space
 */
export function getVertices(object) {
  const vertices = [];

  const geometry = object?.geometry;
  const attrs = geometry?.attributes || {};
  const positionAttribute = attrs.position;
  const startAttribute = attrs.instanceStart;
  const endAttribute = attrs.instanceEnd;

  // Standard geometries (Mesh/Line/LineSegments)
  if (positionAttribute) {
    const vertexCount = positionAttribute.count;
    for (let i = 0; i < vertexCount; i++) {
      const vertex = new THREE.Vector3().fromBufferAttribute(positionAttribute, i);
      vertex.applyMatrix4(object.matrixWorld);
      vertices.push(vertex);
    }
    return vertices;
  }

  // Fat line geometries (Line2/LineSegments2 via LineSegmentsGeometry)
  if (startAttribute && endAttribute) {
    const vertexCount = Math.min(startAttribute.count, endAttribute.count);
    for (let i = 0; i < vertexCount; i++) {
      const a = new THREE.Vector3().fromBufferAttribute(startAttribute, i);
      const b = new THREE.Vector3().fromBufferAttribute(endAttribute, i);
      a.applyMatrix4(object.matrixWorld);
      b.applyMatrix4(object.matrixWorld);
      vertices.push(a, b);
    }
  }
  
  return vertices;
}

/**
 * Calculates the direction vector of a horizontal edge in a segment
 * 
 * This function finds a horizontal edge (where both vertices have approximately
 * the same z-coordinate) in the segment mesh and returns its direction vector.
 * This is used for proper alignment of panels with the segment's orientation.
 * 
 * @param {THREE.Mesh} segment - The segment mesh to analyze
 * @param {THREE.Vector3} normal - The normal vector of the segment plane
 * @returns {THREE.Vector3} Direction vector of a horizontal edge
 */
function getEdgeDirectionOriginal(segment, normal) {
  
  // Initialize edge direction vector
  const edgeDirection = new THREE.Vector3();
  
  // Get all vertices of the segment in world space
  const vertices = getVertices(segment);

  // to store pair of vertices most approximately horizontal
  let minDiff = Infinity;
  let minPair = [];

  // Loop through all vertex pairs to find a horizontal edge
  for (let i = 0; i < vertices.length; i++) {
    for (let j = i + 1; j < vertices.length; j++) {
      const diff = vertices[i].z - vertices[j].z;
      if (Math.abs(diff) < 1) {
        edgeDirection.subVectors(vertices[i], vertices[j]).normalize();
        const crossProduct = new THREE.Vector3().crossVectors(vertices[i], vertices[j]);
        if (crossProduct.dot(normal) < 0) {
          edgeDirection.negate();
        }
        return edgeDirection;
      }
    }
  }
  return edgeDirection;
}

export function getEdgeDirection(segment, normal, strategy = 'adaptive') {
  const edges = segment?.setBacks?.isSet ? segment?.setBacks?.edgesData : segment?.edges;
  
  if (edges && edges.length > 0) {
    
    // Step 1: Calculate Z-differences for all edges
    const edgeAnalysis = edges.map(edge => {
      const zDiff = Math.abs(edge.point1.z - edge.point2.z);
      const direction = new THREE.Vector3().subVectors(edge.point2, edge.point1).normalize();
      
      // Calculate alignment with cardinal directions (X and Y axes)
      const xAlignment = Math.abs(direction.dot(new THREE.Vector3(1, 0, 0))); // X-axis alignment
      const yAlignment = Math.abs(direction.dot(new THREE.Vector3(0, 1, 0))); // Y-axis alignment
      const cardinalAlignment = Math.max(xAlignment, yAlignment);
      
      return {
        edge,
        zDiff,
        direction,
        xAlignment,
        yAlignment,
        cardinalAlignment
      };
    });
    
    // Step 2: Check if Z-differences are meaningful
    const minZDiff = Math.min(...edgeAnalysis.map(e => e.zDiff));
    const maxZDiff = Math.max(...edgeAnalysis.map(e => e.zDiff));
    const zDiffRange = maxZDiff - minZDiff;
    
    let selectedEdge;
    
    // Step 3: Decide selection criteria based on Z-difference significance
    const Z_DIFF_THRESHOLD = 0.3; // If Z-range is less than 50cm, consider it "flat"
    
    if (zDiffRange < Z_DIFF_THRESHOLD) {
      // Case: Relatively flat roof - use cardinal direction alignment
      selectedEdge = edgeAnalysis.reduce((best, current) => {
        return current.cardinalAlignment > best.cardinalAlignment ? current : best;
      }).edge;
            
    } else {
      
      // Find all edges with minimum Z-difference
      const horizontalEdges = edgeAnalysis.filter(e => Math.abs(e.zDiff - minZDiff) < 0.01);
      
      if (horizontalEdges.length === 1) {
        selectedEdge = horizontalEdges[0].edge;
      } else {
        
        selectedEdge = horizontalEdges.reduce((best, current) => {
          return current.cardinalAlignment > best.cardinalAlignment ? current : best;
        }).edge;
      }
    }
    
    const direction = new THREE.Vector3().subVectors(selectedEdge.point2, selectedEdge.point1).normalize();
    return direction;
  }
  
  return getEdgeDirectionOriginal(segment, normal);
}

/**
 * Calculates the rotation quaternion to align a panel with a segment edge
 * 
 * This function computes the rotation needed to align the panel's local x-axis
 * with the direction of an edge in the segment. It returns both the sign of the
 * rotation (which affects offset calculations) and the rotation quaternion.
 * 
 * @param {THREE.Vector3} normal - The normal vector of the segment plane
 * @param {THREE.Vector3} direction - The direction vector of the segment edge
 * @param {THREE.Quaternion} quaternion - The initial quaternion of the panel
 * @returns {Array} Array containing [sign, rotationQuaternion]
 */
export function getRotation(normal, direction, quaternion) {
  // Get the panel's local x-axis after applying initial quaternion
  const planeEdgeDirection = new THREE.Vector3(1, 0, 0).applyQuaternion(quaternion);
  
  // Use the segment's normal as the rotation axis
  const rotationAxis = normal;
  
  // Calculate the angle between the panel's x-axis and the segment edge direction
  const angle = planeEdgeDirection.angleTo(direction);
  
  // Calculate cross product to determine rotation direction
  const crossProduct = new THREE.Vector3().crossVectors(planeEdgeDirection, direction);
  
  // Determine if rotation should be clockwise or counter-clockwise
  // This affects both the rotation and the offset calculation
  const sign = crossProduct.dot(rotationAxis) < 0 ? -1 : 1;
  
  // Create a quaternion representing the rotation around the normal vector
  const rotationQuaternion = new THREE.Quaternion().setFromAxisAngle(rotationAxis, sign * angle);

  // Return both the sign (for offset calculations) and the rotation quaternion
  return [sign, rotationQuaternion];
}
